import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CameraIcon, CheckIcon, ChevronDownIcon, Share2Icon } from 'lucide-react';
import { CommunityGlyph } from '../icons/BeelyvIcons';
import { Button } from '../ui/Button';
import { useLocale } from '../../contexts/LocaleContext';
import { useEngagement } from '../../contexts/EngagementContext';
import type { CommunityChallenge } from '../../types/engagement';
import { challengeTimeline, checkpointState, parseChallengeDate, type CheckpointState } from '../../utils/challenges';
const checkpointTone: Record<CheckpointState, string> = {
  reached: 'border-gold bg-gold text-deep',
  below: 'border-dashed border-deep/35 bg-white text-deep',
  current: 'border-deep bg-white text-deep',
  upcoming: 'border-sand-line bg-sand-deep/60 text-muted'
};

/**
 * One optional community challenge — weekly, monthly or 12 months, same model.
 * Progress is private; sharing and photo submission are always explicit actions.
 */
export function ChallengeCard({
  challenge
}: {
  challenge: CommunityChallenge;
}) {
  const {
    t,
    tl,
    locale
  } = useLocale();
  const navigate = useNavigate();
  const engagement = useEngagement();
  const [shared, setShared] = useState(false);
  const [showCheckpoints, setShowCheckpoints] = useState(false);
  const joined = engagement.joinedChallenges.includes(challenge.id);
  const ratio = Math.min(1, challenge.progress / challenge.target);
  const done = joined && challenge.progress >= challenge.target;
  const timeline = challengeTimeline(challenge);
  const dateLocale = locale === 'pt-BR' ? ptBR : undefined;
  const start = parseChallengeDate(challenge.startDate);
  const end = parseChallengeDate(challenge.endDate);
  const dayFmt = locale === 'pt-BR' ? "d 'de' MMM" : 'MMM d';
  const period = t('eng.ch.period', {
    from: format(start, dayFmt, {
      locale: dateLocale
    }),
    to: format(end, challenge.duration === 'annual' ? `${dayFmt} yyyy` : dayFmt, {
      locale: dateLocale
    })
  });
  const unit = t(`eng.ch.unit.${challenge.metric}`);
  const year = start.getFullYear();
  const timeLabel = timeline.status === 'ended' ? t('eng.ch.ended') : timeline.status === 'upcoming' ? t('eng.ch.startsIn', {
    n: timeline.remainingDays - timeline.totalDays + 1
  }) : timeline.remainingDays === 0 ? t('eng.ch.endsToday') : t('eng.ch.ends', {
    n: timeline.remainingDays
  });
  return <article className="rounded-card border border-sand-line bg-white/70 p-2" data-id="element-657" data-mp="1ec8q3yrz9n30">
      <div className="flex items-start justify-between gap-2" data-id="element-658" data-mp="1eebqncqraylz">
        <div className="min-w-0" data-id="element-659" data-mp="1jasb9wyqyuub">
          <p className="flex flex-wrap items-center gap-x-1 text-[11px] text-muted" data-id="element-660" data-mp="1jdy2piuum8t7">
            <span className="rounded-pill border border-deep/20 px-[8px] py-[2px] text-[10.5px] font-medium text-deep" data-id="element-661" data-mp="1nb2ite26ttba">
              {t(`eng.ch.duration.${challenge.duration}`)}
            </span>
            <span className="bee-num" data-id="element-662" data-mp="2yuf1nb8czqtz">{period}</span>
          </p>
          <h3 className="mt-1 text-[14px] font-bold text-deep" data-id="element-663" data-mp="n3xfuozesito">{tl(challenge.title)}</h3>
          <p className="mt-[2px] text-[12px] leading-snug text-muted" data-id="element-664" data-mp="j355ttipq9wc">{tl(challenge.description)}</p>
        </div>
        {done && <span className="bee-overline shrink-0 rounded-pill bg-gold px-2 py-[4px] text-[8.5px] font-bold text-deep" data-id="element-665" data-mp="eau72tzpku5d">
            {t('eng.ch.done')}
          </span>}
      </div>

      {joined && <div className="mt-2" data-id="element-666" data-mp="4cecgh1h32r4">
          <p className="bee-num text-[12px] font-medium text-deep" data-id="element-667" data-mp="3vl1yz0bg8w09">
            {t('eng.ch.progress', {
          current: challenge.progress,
          target: challenge.target,
          unit
        })}
          </p>
          <div className="mt-1 h-[5px] overflow-hidden rounded-pill bg-sand-deep" role="progressbar" aria-valuemin={0} aria-valuemax={challenge.target} aria-valuenow={challenge.progress} aria-label={tl(challenge.title)} data-id="element-668" data-mp="3rl1jrdgj0gs5">
            <div className={`h-full rounded-pill ${done ? 'bg-gold' : 'bg-soft'}`} style={{
          width: `${ratio * 100}%`
        }} data-id="element-669" data-mp="hhtowmmy1v3l" />
          </div>

          {challenge.duration === 'annual' && <div className="mt-2" data-id="element-670" data-mp="8hy7w16pj5vn">
              <div className="flex items-center justify-between gap-2 text-[11px] text-muted" data-id="element-671" data-mp="8gvjbzwuxfiw">
                <span className="bee-num" data-id="element-672" data-mp="deorgyhq0jlt">
                  {t('eng.ch.elapsed', {
              elapsed: timeline.elapsedDays,
              total: timeline.totalDays
            })}
                </span>
                <span className="bee-num" data-id="element-673" data-mp="3ibu9qmyvnmb">{t('eng.ch.remaining', {
              n: timeline.remainingDays
            })}</span>
              </div>
              <div className="mt-1 h-[3px] overflow-hidden rounded-pill bg-sand-deep" aria-hidden="true" data-id="element-674" data-mp="3lxmh8p4djh0e">
                <div className="h-full bg-deep/40" style={{
            width: `${timeline.elapsedRatio * 100}%`
          }} data-id="element-675" data-mp="3py6gpzcf6wpp" />
              </div>

              {challenge.checkpoints && <div className="mt-2" data-id="element-676" data-mp="3gx8arbif8f4h">
                  <button type="button" onClick={() => setShowCheckpoints(open => !open)} aria-expanded={showCheckpoints} className="flex min-h-[36px] items-center gap-1 text-[12px] font-medium text-deep" data-id="element-677" data-mp="3gz1fumddyrad">
                    {showCheckpoints ? t('eng.ch.checkpoints.hide') : t('eng.ch.checkpoints.show')}
                    <ChevronDownIcon size={14} strokeWidth={1.6} aria-hidden="true" className={`transition-transform duration-150 ease-brand ${showCheckpoints ? 'rotate-180' : ''}`} data-id="element-678" data-mp="32ccfkibb8a2x" />
                  </button>
                  {showCheckpoints && <>
                      <ol className="mt-1 grid grid-cols-6 gap-1" data-id="element-679" data-mp="3l1j2yin1tpv2">
                        {challenge.checkpoints.map(checkpoint => {
                const state = checkpointState(checkpoint, year);
                const month = format(new Date(year, checkpoint.month, 1), 'MMM', {
                  locale: dateLocale
                });
                return <li key={checkpoint.month} aria-label={t('eng.ch.cp.aria', {
                  month,
                  progress: checkpoint.progress,
                  target: checkpoint.target,
                  state: t(`eng.ch.cp.${state}`)
                })} className={`flex flex-col items-center rounded-[10px] border px-[2px] py-[5px] ${checkpointTone[state]}`} data-id="element-680" data-mp="awvml4dl76dg">
                              <span className="text-[10px] font-medium capitalize leading-none" data-id="element-681" data-mp="13k7xghadxpqx">{month}</span>
                              <span className="bee-num mt-[3px] text-[10px] leading-none" data-id="element-682" data-mp="30db8cz2d732y">
                                {state === 'upcoming' ? '—' : checkpoint.progress}
                              </span>
                            </li>;
              })}
                      </ol>
                      <p className="mt-1 text-[11px] leading-snug text-muted" data-id="element-683" data-mp="1hp88w9ssi2er">
                        {challenge.checkpointsRequired ? t('eng.ch.checkpoints.required') : t('eng.ch.checkpoints.flexible')}
                      </p>
                    </>}
                </div>}
            </div>}
        </div>}

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted" data-id="element-684" data-mp="21eio7momilcc">
        <span className="bee-num flex items-center gap-1" data-id="element-685" data-mp="1wdmollbb1lcq">
          <CommunityGlyph size={16} aria-hidden="true" data-id="element-686" data-mp="1rg34w5vu8gde" />
          {t('eng.ch.participants', {
          n: challenge.participants.toLocaleString(locale)
        })}
        </span>
        <span aria-hidden="true" data-id="element-687" data-mp="kxo5spqbrkyb">·</span>
        <span className="bee-num" data-id="element-688" data-mp="g04rxqxjha6w">{timeLabel}</span>
      </div>

      <div className="mt-2 flex flex-wrap gap-2" data-id="element-689" data-mp="g2qxjl0vdfmh">
        <Button variant={joined ? 'outline' : 'primary'} className="flex-1" aria-pressed={joined} onClick={() => engagement.toggleChallenge(challenge.id)} data-id="element-690" data-mp="2g1vzg077y29h">
          {joined && <CheckIcon size={15} className="text-gold-deep" aria-hidden="true" data-id="element-691" data-mp="26618mhsud0vm" />}
          {joined ? t('eng.ch.joined') : t('eng.ch.join')}
        </Button>
        {joined && challenge.poseGuide && <Button variant="outline" className="flex-1" onClick={() => navigate(`/desafios/${challenge.id}/foto`)} data-id="element-692" data-mp="2ytx6hpu5cytz">
            <CameraIcon size={15} strokeWidth={1.6} aria-hidden="true" data-id="element-693" data-mp="2p45aqsab9znx" />
            {t('eng.ch.photo.cta')}
          </Button>}
        {done && <Button variant="quiet" className="flex-1" onClick={() => setShared(true)} disabled={shared} data-id="element-694" data-mp="2u0lwjfu1g1xu">
            <Share2Icon size={15} aria-hidden="true" data-id="element-695" data-mp="3hq6jsax85lcx" />
            {shared ? t('eng.ch.shared') : t('eng.ch.share')}
          </Button>}
      </div>
    </article>;
}